import { useEffect, useMemo, useState } from "react";
import { Backdrop, Button, CircularProgress, Typography } from "@mui/material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { saveAs } from "file-saver";

import { findAllAcoesQualificacao } from "API/apiAcoes";
import {
  findRelatorioMigracaoEducaSuas,
  findRelatorioMigracaoEducaSuasCount,
  findTurmasRelatorioGeral,
  RelatorioMigracaoEducaSuasParams,
} from "API/apiRelatoriosTurma";
import MultiSelectCheck from "components/atoms/MultiSelectField/MultSelectCheck";
import { MultiSelectOption } from "components/atoms/MultiSelectField/MultiSelectOptions";
import { useAppFeedback } from "hooks/useAppFeedback";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";

import styles from "../Relatorios.module.css";

type RelatorioMigracaoEducaSuasProps = {
  selectedReportLabel: string;
};

type ItemMigracao = {
  acao_id?: number | null;
  acao_nome?: string | null;
  turma_id?: number | null;
  turma_nome?: string | null;
  turma_data_inicio?: string | null;
  turma_data_fim?: string | null;
  user_id?: number | null;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  cpf?: string | null;
};

const sanitizeCpf = (value?: string | null) => (value || "").replace(/\D/g, "");

const buildCsv = (items: ItemMigracao[]) => {
  const escapeValue = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const lines = [
    ["username", "password", "firstname", "lastname", "email", "course1"].join(";"),
    ...items.map((item) =>
      [
        sanitizeCpf(item.cpf),
        "",
        item.firstname || "",
        item.lastname || "",
        item.email || "",
        "",
      ]
        .map((value) => escapeValue(String(value)))
        .join(";"),
    ),
  ];

  return `\uFEFF${lines.join("\r\n")}`;
};

const RelatorioMigracaoEducaSuas = ({ selectedReportLabel }: RelatorioMigracaoEducaSuasProps) => {
  const { showError, showSuccess } = useAppFeedback();
  const [acoesSelecionadas, setAcoesSelecionadas] = useState<Array<number | string>>([]);
  const [turmasSelecionadas, setTurmasSelecionadas] = useState<Array<number | string>>([]);
  const [acoesOptions, setAcoesOptions] = useState<MultiSelectOption[]>([]);
  const [turmasOptions, setTurmasOptions] = useState<MultiSelectOption[]>([]);
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);

  const selectedActionIds = useMemo(
    () => acoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [acoesSelecionadas],
  );
  const selectedTurmaIds = useMemo(
    () => turmasSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [turmasSelecionadas],
  );

  useEffect(() => {
    const loadAcoes = async () => {
      try {
        setLoadingAcoes(true);
        const response = await findAllAcoesQualificacao(1, 999999);
        setAcoesOptions((response.acoes || []).map((item) => ({ id: item.id, nome: item.nome })));
      } catch (error) {
        setAcoesOptions([]);
        showError("Não foi possível carregar as ações do relatório.", error);
      } finally {
        setLoadingAcoes(false);
      }
    };

    void loadAcoes();
  }, [showError]);

  useEffect(() => {
    if (selectedActionIds.length === 0) {
      setTurmasOptions([]);
      setTurmasSelecionadas([]);
      setEstimatedCount(null);
      return;
    }

    const loadTurmas = async () => {
      try {
        setLoadingTurmas(true);
        const response = await findTurmasRelatorioGeral(selectedActionIds);
        const turmas = response.turmas || [];
        setTurmasOptions(turmas.map((item) => ({ id: item.id, nome: item.nome })));
        setTurmasSelecionadas((current) =>
          current.filter((id) => turmas.some((turma) => turma.id === Number(id))),
        );
      } catch (error) {
        setTurmasOptions([]);
        setTurmasSelecionadas([]);
        showError("Não foi possível carregar as turmas do relatório.", error);
      } finally {
        setLoadingTurmas(false);
      }
    };

    void loadTurmas();
  }, [selectedActionIds, showError]);

  const requestParams = useMemo<RelatorioMigracaoEducaSuasParams | null>(() => {
    if (selectedActionIds.length === 0) {
      return null;
    }

    return {
      acoes_ids: selectedActionIds,
      turmas_ids: selectedTurmaIds.length > 0 ? selectedTurmaIds : undefined,
    };
  }, [selectedActionIds, selectedTurmaIds]);

  useEffect(() => {
    if (!requestParams) {
      setEstimatedCount(null);
      return;
    }

    const loadCount = async () => {
      try {
        setLoadingCount(true);
        const response = await findRelatorioMigracaoEducaSuasCount(requestParams);
        setEstimatedCount(response.count ?? 0);
      } catch (error) {
        setEstimatedCount(null);
        showError("Não foi possível calcular o resultado estimado do relatório.", error);
      } finally {
        setLoadingCount(false);
      }
    };

    void loadCount();
  }, [requestParams, showError]);

  const handleClear = () => {
    setAcoesSelecionadas([]);
    setTurmasSelecionadas([]);
    setTurmasOptions([]);
    setEstimatedCount(null);
  };

  const handleGenerateReport = async () => {
    if (!requestParams) {
      showError("Selecione ao menos uma ação para gerar o relatório.");
      return;
    }

    try {
      setGeneratingReport(true);
      const response = await findRelatorioMigracaoEducaSuas(requestParams);
      const csvContent = buildCsv((response.data || []) as ItemMigracao[]);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "relatorio_migracao_educasuas_mg.csv");
      showSuccess("Relatório gerado com sucesso.");
    } catch (error) {
      showError("Não foi possível gerar o relatório.", error);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <>
      <div className={styles.reportFilterBody}>
        <div className={styles.reportHeaderRow}>
          <div className={styles.reportSelectedWrap}>
            <span className={styles.reportSelectedLabel}>Relatório selecionado:</span>
            <span className={styles.reportSelectedChip}>{selectedReportLabel}</span>
          </div>

          <div className={styles.exportFormatCard}>
            <div className={styles.exportFormatLabel}>Formato de exportação</div>
            <div className={styles.exportFormatValue}>
              <TableChartOutlinedIcon className={styles.exportFormatIcon} />
              CSV
            </div>
          </div>
        </div>

        <div className={styles.reportDivider}></div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Selecione a(s) ação(ões) <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder="Selecione uma ou mais ações"
            fullWidth
            value={acoesSelecionadas}
            onChange={setAcoesSelecionadas}
            options={acoesOptions}
            disabled={loadingAcoes}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Selecione a(s) turma(s) <span className={styles.optionalText}>(opcional)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder={
              selectedActionIds.length > 0
                ? "Selecione uma ou mais turmas"
                : "Selecione primeiro uma ou mais ações"
            }
            fullWidth
            value={turmasSelecionadas}
            onChange={setTurmasSelecionadas}
            options={turmasOptions}
            disabled={selectedActionIds.length === 0 || loadingTurmas}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.reportInfoBanner}>
          <InfoOutlinedIcon className={styles.reportInfoBannerIcon} />
          <span>
            O CSV será exportado no padrão Moodle, separado por ponto e vírgula, com colunas username, password, firstname,
            lastname, email e course1.
          </span>
        </div>

        <div className={styles.selectionSummaryCard}>
          <div className={styles.selectionSummaryTitle}>Resumo da seleção</div>
          <div className={styles.selectionSummaryGrid}>
            <div className={styles.selectionSummaryItem}>
              <AppsOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Ações selecionadas</div>
                <div className={styles.selectionSummaryValue}>{selectedActionIds.length} ação(ões)</div>
              </div>
            </div>

            <div className={styles.selectionSummaryItem}>
              <GroupsOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Turmas selecionadas</div>
                <div className={styles.selectionSummaryValue}>
                  {selectedTurmaIds.length > 0 ? `${selectedTurmaIds.length} turma(s)` : "Todas as turmas"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reportActionsRow}>
          <Button
            variant="outlined"
            className={stylesButton.buttonSoftMuted}
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>

          <div className={styles.reportActionsRight}>
            <div className={styles.estimatedResultCard}>
              <div className={styles.estimatedResultLabel}>Resultado estimado</div>
              <div className={styles.estimatedResultValue}>
                {loadingCount ? <CircularProgress size={18} /> : estimatedCount ?? "-"}
              </div>
              <div className={styles.estimatedResultMeta}>registros</div>
            </div>

            <div className={styles.generateButtonGroup}>
              <Button
                variant="contained"
                className={styles.generateButton}
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport}
              >
                Gerar relatório
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        open={generatingReport}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 100,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>Gerando relatório...</Typography>
      </Backdrop>
    </>
  );
};

export default RelatorioMigracaoEducaSuas;
